import { FC, useRef, useState } from 'react';

import { useApplication } from '@eneco/common/application';
import { Recaptcha, RecaptchaRef } from '@eneco/components/Recaptcha/Recaptcha';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { postEnecoBeXapiSiteApiV1ContactForm } from '@eneco/dc-be/client';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import {
  AlertDialog,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Grid,
  Heading,
  InputText,
  NotificationBox,
  PageGrid,
  Stack,
  TextArea,
  TextLink,
} from '@eneco/sparky';
import { styled } from '@eneco/sparky/stitches';
import { NotificationBoxProps } from '@eneco/sparky/types';
import { useContent } from '@sitecore/common';
import { CancelOrderRendering } from '@sitecore/types/CancelOrder';
import { Controller, FormProvider, SubmitHandler, useForm, useFormContext } from 'react-hook-form';
import * as yup from 'yup';

import { fetchCancelOrderDetails } from './cancelOrderPrefill';
import {
  type CancelOrderLockedFields,
  createCancelOrderLockedFields,
  createCancelOrderPrefillValues,
} from './cancelOrderPrefillValues';
import { submitCancelOrder } from './cancelOrderSubmit';
import Banner from './components/Banner';
import { InputDigits } from '../../components/InputDigits';
import PhoneNumberInputField, { phoneValidationScheme } from '../../components/PhoneNumberInputField';
import ReadFormField from '../../components/ReadFormField';
import { useRedirectAndNotifyBE } from '../../hooks/useRedirectAndNotifyBE';
import AddressAutocomplete from '../../utils/addresses/AddressAutocomplete';
import { languageToContactFormLanguageDto } from '../../utils/language';
import { RecaptchaFormBe } from '../../utils/reCaptcha/ReCaptchaFormBe';
import '../../utils/customYupValidation';
import { link } from '../../utils/richTextReplacements';

export const CANCEL_ORDER_SCHEMA_FIELDS = {
  ORDER_NUMBER: 'orderNumber',
  FIRST_NAME: 'firstName',
  LAST_NAME_ORDER_ID: 'lastNameOrderId',
  CUSTOMER_NUMBER: 'customerNumber',
  ZIP_CODE: 'zipCode',
  CITY: 'city',
  IS_API_POSTAL_CODE_CITY_VALID: 'isApiPostalCodeCityValid',
  STREET: 'street',
  HOUSE_NUMBER: 'houseNumber',
  BUS_NUMBER: 'busNumber',
  EMAIL: 'email',
  PHONE_NUMBER: 'phoneNumber',
  PRODUCT: 'product',
  CANCELLATION_REASON: 'cancellationReason',
  RECAPTCHA_TOKEN: 'recaptchaToken',
} as const;

const getCancelOrderSchema = (fields: CancelOrderRendering['fields']['data']) =>
  yup.object({
    [CANCEL_ORDER_SCHEMA_FIELDS.ORDER_NUMBER]: yup
      .string()
      .required(fields.orderNumberFormField.value.requiredMessage ?? 'Vul je bestelnummer in [not in sitecore]'),
    [CANCEL_ORDER_SCHEMA_FIELDS.FIRST_NAME]: yup.string(),
    [CANCEL_ORDER_SCHEMA_FIELDS.LAST_NAME_ORDER_ID]: yup
      .string()
      .required(fields.lastNameFormField.value.requiredMessage ?? 'Vul je achternaam in [not in sitecore]'),
    [CANCEL_ORDER_SCHEMA_FIELDS.CUSTOMER_NUMBER]: yup.string(),
    [CANCEL_ORDER_SCHEMA_FIELDS.ZIP_CODE]: yup
      .string()
      .required(fields.zipCodeFormField.value.requiredMessage ?? 'Vul je postcode in [not in sitecore]'),
    [CANCEL_ORDER_SCHEMA_FIELDS.CITY]: yup
      .string()
      .required(fields.cityFormField.value.requiredMessage ?? 'Vul je stad in [not in sitecore]'),
    [CANCEL_ORDER_SCHEMA_FIELDS.IS_API_POSTAL_CODE_CITY_VALID]: yup
      .boolean()
      .oneOf([true], fields.postalCodeCheckNotification.value.content ?? 'Postcodecontrole mislukt [not in sitecore]')
      .required(fields.zipCodeFormField.value.requiredMessage ?? 'Vul je postcode in [not in sitecore]'),
    [CANCEL_ORDER_SCHEMA_FIELDS.STREET]: yup
      .string()
      .required(fields.streetFormField.value.requiredMessage ?? 'Vul je straat in [not in sitecore]'),
    [CANCEL_ORDER_SCHEMA_FIELDS.HOUSE_NUMBER]: yup
      .string()
      .required(fields.houseNumberFormField.value.requiredMessage ?? 'Vul je huisnummer in [not in sitecore]'),
    [CANCEL_ORDER_SCHEMA_FIELDS.BUS_NUMBER]: yup.string(),
    // Missing prefilled values stay editable, so email is always required
    [CANCEL_ORDER_SCHEMA_FIELDS.EMAIL]: yup
      .string()
      .validEmail(fields.emailFormField.value.validationMessage ?? 'Vul een geldig e-mailadres in [not in sitecore]')
      .required(fields.emailFormField.value.requiredMessage ?? 'Vul je e-mailadres in [not in sitecore]'),
    [CANCEL_ORDER_SCHEMA_FIELDS.PHONE_NUMBER]: phoneValidationScheme({
      validationMessage:
        fields.phoneNumberFormField.value.validationMessage ?? 'Vul een geldig telefoonnummer in [not in sitecore]',
      isRequired: false,
    }),
    [CANCEL_ORDER_SCHEMA_FIELDS.PRODUCT]: yup
      .array()
      .of(yup.string().oneOf(['electricity', 'gas']))
      .min(1, fields.productFormField.value.requiredMessage || 'Kies minimaal één product [not in sitecore]'),
    [CANCEL_ORDER_SCHEMA_FIELDS.CANCELLATION_REASON]: yup.string(),
    [CANCEL_ORDER_SCHEMA_FIELDS.RECAPTCHA_TOKEN]: yup.string().nullable(),
  });

export type CancelOrderValues = yup.InferType<ReturnType<typeof getCancelOrderSchema>>;

const CancelOrder: FC = () => {
  const { fields } = useContent<CancelOrderRendering>();

  const contactDataSchema = getCancelOrderSchema(fields.data);
  const resolver = typedYupResolver<CancelOrderValues>(contactDataSchema);

  const form = useForm<CancelOrderValues>({
    resolver,
    defaultValues: { product: [], isApiPostalCodeCityValid: false },
  });

  return (
    <>
      <Banner />
      <Box backgroundColor="backgroundPrimary" paddingY={16}>
        <PageGrid>
          <Grid.Item gridColumn={{ initial: '1/-1', md: '3/-3' }}>
            <FormProvider {...form}>
              <CancelOrderContent />
            </FormProvider>
          </Grid.Item>
        </PageGrid>
      </Box>
    </>
  );
};

interface OrderNumberHintProps {
  content: string;
  title: string;
  triggerText: string;
}

export const OrderNumberHint: FC<OrderNumberHintProps> = ({ content, title, triggerText }) => (
  <TrackedDialog
    description={<RichText html={content} />}
    title={title}
    trigger={
      <TextLink emphasis="low">
        <RichText html={triggerText} />
      </TextLink>
    }
  />
);

const CancelOrderContent = () => {
  const { fields } = useContent<CancelOrderRendering>();
  const { language } = useApplication();
  const { format } = useFormatter();
  const redirectAndNotify = useRedirectAndNotifyBE();

  const data = fields.data;
  const orderNumberDialog = data?.orderNumberDialog?.value;
  const confirmationDialog = data?.confirmationDialog?.value;
  const privacyStatementContent = data.privacyStatementContent?.value?.includes('{url}')
    ? data.privacyStatementContent.value
    : `Eneco mag mijn gegevens gebruiken zoals aangegeven in Eneco's {url} [not in sitecore]`;

  const recaptchaRef = useRef<RecaptchaRef>(null);
  const [isLoadingCustomerData, setIsLoadingCustomerData] = useState<boolean>(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [lockedFields, setLockedFields] = useState<CancelOrderLockedFields | null>(null);
  const isCustomerDataPrefilled = lockedFields !== null;
  const isZipCodeCityLocked = Boolean(lockedFields?.zipCode && lockedFields?.city);
  // Bus number is optional and locked after prefill, except when the street is missing: then the address is completed manually
  const isBusNumberLocked = Boolean(lockedFields?.busNumber && lockedFields?.street);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState<CancelOrderValues | null>(null);
  const [customerDataError, setCustomerDataError] = useState<boolean>(false);
  const [lastNameOrderIdError, setLastNameOrderIdError] = useState<string | null>(null);
  const [prefilledLastName, setPrefilledLastName] = useState<string | null>(null);
  const [addressAutocompletePrefill, setAddressAutocompletePrefill] = useState({
    key: 0,
    zipCode: '',
    city: '',
    street: '',
  });
  const [error, setError] = useState<NotificationBoxProps | null>(null);

  const {
    register,
    control,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<CancelOrderValues>();

  const resetPrefilledCustomerData = () => {
    if (!isCustomerDataPrefilled) return;

    setValue(CANCEL_ORDER_SCHEMA_FIELDS.CUSTOMER_NUMBER, '');
    setValue(CANCEL_ORDER_SCHEMA_FIELDS.FIRST_NAME, '');
    setValue(CANCEL_ORDER_SCHEMA_FIELDS.ZIP_CODE, '');
    setValue(CANCEL_ORDER_SCHEMA_FIELDS.CITY, '');
    setValue(CANCEL_ORDER_SCHEMA_FIELDS.IS_API_POSTAL_CODE_CITY_VALID, false);
    setValue(CANCEL_ORDER_SCHEMA_FIELDS.STREET, '');
    setValue(CANCEL_ORDER_SCHEMA_FIELDS.HOUSE_NUMBER, '');
    setValue(CANCEL_ORDER_SCHEMA_FIELDS.BUS_NUMBER, '');
    setValue(CANCEL_ORDER_SCHEMA_FIELDS.EMAIL, '');
    setValue(CANCEL_ORDER_SCHEMA_FIELDS.PHONE_NUMBER, '');
    setValue(CANCEL_ORDER_SCHEMA_FIELDS.PRODUCT, []);
    setAddressAutocompletePrefill(currentPrefill => ({
      key: currentPrefill.key + 1,
      zipCode: '',
      city: '',
      street: '',
    }));
    setLockedFields(null);
    setCustomerDataError(false);
    setPrefilledLastName(null);
  };

  const orderNumberRegistration = register(CANCEL_ORDER_SCHEMA_FIELDS.ORDER_NUMBER);
  const lastNameOrderIdRegistration = register(CANCEL_ORDER_SCHEMA_FIELDS.LAST_NAME_ORDER_ID);

  const fetchCustomerData = async () => {
    setCustomerDataError(false);

    const hasValidOrderNumber = await trigger(CANCEL_ORDER_SCHEMA_FIELDS.ORDER_NUMBER);
    const lastName = (getValues(CANCEL_ORDER_SCHEMA_FIELDS.LAST_NAME_ORDER_ID) ?? '').trim();

    if (!lastName) {
      setLastNameOrderIdError(
        fields.data.lastNameFormField.value.requiredMessage ?? 'Vul je achternaam in [not in sitecore]',
      );
    }

    if (!hasValidOrderNumber || !lastName) return;

    setLastNameOrderIdError(null);
    const salesOrderNumber = getValues(CANCEL_ORDER_SCHEMA_FIELDS.ORDER_NUMBER).trim();
    setIsLoadingCustomerData(true);

    try {
      const customerData = await fetchCancelOrderDetails(salesOrderNumber, lastName);

      const hasIdentificationChanged =
        getValues(CANCEL_ORDER_SCHEMA_FIELDS.ORDER_NUMBER).trim() !== salesOrderNumber ||
        (getValues(CANCEL_ORDER_SCHEMA_FIELDS.LAST_NAME_ORDER_ID) ?? '').trim() !== lastName;

      if (hasIdentificationChanged) return;

      if (!customerData) {
        setCustomerDataError(true);
        return;
      }

      const products: CancelOrderValues['product'] = [];
      if (customerData.products.some(product => ['Electricity', 'Both'].includes(product.energyType))) {
        products.push('electricity');
      }
      if (customerData.products.some(product => ['Gas', 'Both'].includes(product.energyType))) {
        products.push('gas');
      }

      const prefillValues = createCancelOrderPrefillValues(customerData);
      const nextLockedFields = createCancelOrderLockedFields(prefillValues);
      setPrefilledLastName(prefillValues.lastName || null);
      setValue(CANCEL_ORDER_SCHEMA_FIELDS.CUSTOMER_NUMBER, prefillValues.customerNumber, { shouldValidate: true });
      setValue(CANCEL_ORDER_SCHEMA_FIELDS.FIRST_NAME, prefillValues.firstName, { shouldValidate: true });
      setValue(CANCEL_ORDER_SCHEMA_FIELDS.ZIP_CODE, prefillValues.zipCode, { shouldValidate: true });
      setValue(CANCEL_ORDER_SCHEMA_FIELDS.CITY, prefillValues.city, { shouldValidate: true });
      // Only trust the postal code/city when both came back; otherwise the address lookup validates the input
      setValue(
        CANCEL_ORDER_SCHEMA_FIELDS.IS_API_POSTAL_CODE_CITY_VALID,
        nextLockedFields.zipCode && nextLockedFields.city,
        { shouldValidate: true },
      );
      setValue(CANCEL_ORDER_SCHEMA_FIELDS.STREET, prefillValues.street, { shouldValidate: true });
      setValue(CANCEL_ORDER_SCHEMA_FIELDS.HOUSE_NUMBER, prefillValues.houseNumber, { shouldValidate: true });
      setValue(CANCEL_ORDER_SCHEMA_FIELDS.BUS_NUMBER, prefillValues.busNumber, { shouldValidate: true });
      setValue(CANCEL_ORDER_SCHEMA_FIELDS.EMAIL, prefillValues.email, { shouldValidate: true });
      setValue(CANCEL_ORDER_SCHEMA_FIELDS.PHONE_NUMBER, prefillValues.phoneNumber, { shouldValidate: true });
      setValue(CANCEL_ORDER_SCHEMA_FIELDS.PRODUCT, products, { shouldValidate: true });
      setAddressAutocompletePrefill(currentPrefill => ({
        key: currentPrefill.key + 1,
        zipCode: prefillValues.zipCode,
        city: prefillValues.city,
        street: prefillValues.street,
      }));
      setLockedFields(nextLockedFields);
    } catch {
      const hasIdentificationChanged =
        getValues(CANCEL_ORDER_SCHEMA_FIELDS.ORDER_NUMBER).trim() !== salesOrderNumber ||
        (getValues(CANCEL_ORDER_SCHEMA_FIELDS.LAST_NAME_ORDER_ID) ?? '').trim() !== lastName;

      if (!hasIdentificationChanged) {
        setCustomerDataError(true);
      }
    } finally {
      setIsLoadingCustomerData(false);
    }
  };

  const submitForm: SubmitHandler<CancelOrderValues> = values => {
    setPendingSubmitValues(values);
    setIsConfirmationOpen(true);
  };

  const confirmSubmit = async () => {
    if (!pendingSubmitValues) return;

    setIsLoadingSubmit(true);
    setError(null);

    try {
      await submitCancelOrder(pendingSubmitValues, postEnecoBeXapiSiteApiV1ContactForm, {
        language: languageToContactFormLanguageDto(language),
        subject: data.contactAppSubjectText.value ?? 'Contract en tarieven / Contrat et tarifs',
        subLevelSubject: data.contactAppSubLevelSubjectText.value ?? 'Contract herroepen / Rétractation du contrat',
        lastName: prefilledLastName ?? pendingSubmitValues.lastNameOrderId,
      });
      setPendingSubmitValues(null);
      redirectAndNotify({
        route: data.redirectAfterSuccessLink.value.href ?? '/ [not in sitecore]',
        text: <RichText html={data.successNotification.value.content ?? 'Succesmelding [not in sitecore]'} />,
        title: data.successNotification.value.title ?? 'Succes [not in sitecore]',
        variant: data.successNotification.value.variant ?? 'success',
      });
    } catch {
      setError({
        isAlert: false,
        text: data.errorNotification.value.content ?? 'Foutmelding [not in sitecore]',
        title: data.errorNotification.value.title ?? 'Fout [not in sitecore]',
        variant: data.errorNotification.value.variant ?? 'error',
      });
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  return (
    <RecaptchaFormBe recaptchaRef={recaptchaRef} submitForm={submitForm}>
      <Stack gap="10">
        <Stack.Item>
          <Stack gap={4}>
            <Heading size="S" as="h2">
              {data.identificationTitle.value ?? 'Identificatie [not in sitecore]'}
            </Heading>
            <span>{data.identificationDescription.value ?? 'Identificatie beschrijving [not in sitecore]'}</span>
            <InputText
              error={errors.orderNumber?.message as string}
              label={data.orderNumberFormField.value.label ?? 'Bestelnummer [not in sitecore]'}
              placeholder={data.orderNumberFormField.value.placeholder ?? 'Bestelnummer placeholder [not in sitecore]'}
              hint={
                <OrderNumberHint
                  content={orderNumberDialog.content ?? 'Bestelnummer uitleg [not in sitecore]'}
                  title={orderNumberDialog.title ?? 'Bestelnummer terugvinden [not in sitecore]'}
                  triggerText={orderNumberDialog.triggerText ?? 'Bestelnummer hulp [not in sitecore]'}
                />
              }
              {...orderNumberRegistration}
              onChange={event => {
                orderNumberRegistration.onChange(event);
                resetPrefilledCustomerData();
              }}
            />
            <InputText
              error={lastNameOrderIdError ?? (errors.lastNameOrderId?.message as string)}
              label={data.lastNameFormField.value.label ?? 'Achternaam [not in sitecore]'}
              placeholder={data.lastNameFormField.value.placeholder ?? 'Achternaam placeholder [not in sitecore]'}
              hint={data.lastNameFormField.value.hint ?? 'Achternaam hulp [not in sitecore]'}
              {...lastNameOrderIdRegistration}
              onChange={event => {
                lastNameOrderIdRegistration.onChange(event);
                setLastNameOrderIdError(null);
                resetPrefilledCustomerData();
              }}
            />
            {customerDataError && (
              <NotificationBox
                isAlert
                text={
                  <RichText
                    html={
                      data.orderDataErrorNotification.value.content ?? 'Ordergegevens niet gevonden [not in sitecore]'
                    }
                  />
                }
                title={data.orderDataErrorNotification.value.title ?? 'Foutmelding [not in sitecore]'}
                variant={data.orderDataErrorNotification.value.variant ?? 'error'}
              />
            )}
            <Button
              type="button"
              onClick={fetchCustomerData}
              isLoading={isLoadingCustomerData}
              isDisabled={isLoadingCustomerData}>
              {data.getCustomerDataButtonText.value ?? 'Gegevens ophalen [not in sitecore]'}
            </Button>
          </Stack>
        </Stack.Item>

        <Stack.Item>
          <Stack gap={4}>
            <Stack.Item>
              <Stack gap={4}>
                <Heading size="S" as="h2">
                  {data.customerDataTitle.value ?? 'Jouw gegevens [not in sitecore]'}
                </Heading>
                {lockedFields?.customerNumber ? (
                  <ReadFormField
                    label={data.customerNumberFormField.value.label ?? 'Klantnummer [not in sitecore]'}
                    value={getValues(CANCEL_ORDER_SCHEMA_FIELDS.CUSTOMER_NUMBER)}
                  />
                ) : (
                  <InputDigits
                    error={errors.customerNumber?.message as string}
                    label={data.customerNumberFormField.value.label ?? 'Klantnummer [not in sitecore]'}
                    placeholder={
                      data.customerNumberFormField.value.placeholder ?? 'Klantnummer placeholder [not in sitecore]'
                    }
                    hint={
                      <RichText html={data.customerNumberTooltipText.value ?? 'Klantnummer hulp [not in sitecore]'} />
                    }
                    {...register('customerNumber')}
                  />
                )}
                {lockedFields?.firstName ? (
                  <ReadFormField
                    label={data.firstNameFormField.value.label ?? 'Voornaam [not in sitecore]'}
                    value={getValues(CANCEL_ORDER_SCHEMA_FIELDS.FIRST_NAME)}
                  />
                ) : (
                  <InputText
                    error={errors.firstName?.message as string}
                    label={data.firstNameFormField.value.label ?? 'Voornaam [not in sitecore]'}
                    placeholder={data.firstNameFormField.value.placeholder ?? 'Voornaam placeholder [not in sitecore]'}
                    hint={data.firstNameFormField.value.hint ?? 'Voornaam hulp [not in sitecore]'}
                    {...register('firstName')}
                  />
                )}
                {lockedFields?.email ? (
                  <ReadFormField
                    label={data.emailFormField.value.label ?? 'E-mailadres [not in sitecore]'}
                    value={getValues(CANCEL_ORDER_SCHEMA_FIELDS.EMAIL)}
                  />
                ) : (
                  <InputText
                    error={errors.email?.message as string}
                    label={data.emailFormField.value.label ?? 'E-mailadres [not in sitecore]'}
                    placeholder={data.emailFormField.value.placeholder ?? 'E-mailadres placeholder [not in sitecore]'}
                    {...register('email')}
                  />
                )}
                {lockedFields?.phoneNumber ? (
                  <ReadFormField
                    label={data.phoneNumberFormField.value.label ?? 'Telefoonnummer [not in sitecore]'}
                    value={getValues(CANCEL_ORDER_SCHEMA_FIELDS.PHONE_NUMBER)}
                  />
                ) : (
                  <PhoneNumberInputField
                    label={data.phoneNumberFormField.value.label ?? 'Telefoonnummer [not in sitecore]'}
                    hint={
                      <RichText
                        html={data.phoneNumberFormField.value.hint ?? 'Telefoonnummer hulp [not in sitecore]'}
                      />
                    }
                    placeholder={
                      data.phoneNumberFormField.value.placeholder ?? 'Telefoonnummer placeholder [not in sitecore]'
                    }
                    {...register('phoneNumber')}
                  />
                )}
              </Stack>
            </Stack.Item>

            <Stack.Item>
              <Stack gap={4}>
                <Heading size="S" as="h2">
                  {data.deliveryAddressTitle.value ?? 'Leveringsadres [not in sitecore]'}
                </Heading>
                {isZipCodeCityLocked ? (
                  <>
                    <ReadFormField
                      label={data.zipCodeFormField.value.label ?? 'Postcode [not in sitecore]'}
                      value={getValues(CANCEL_ORDER_SCHEMA_FIELDS.ZIP_CODE)}
                    />
                    <ReadFormField
                      label={data.cityFormField.value.label ?? 'Stad [not in sitecore]'}
                      value={getValues(CANCEL_ORDER_SCHEMA_FIELDS.CITY)}
                    />
                  </>
                ) : (
                  <AddressAutocomplete
                    key={addressAutocompletePrefill.key}
                    zipCodeFormField={{
                      label: data.zipCodeFormField.value.label ?? 'Postcode [not in sitecore]',
                      hint: data.zipCodeFormField.value.hint ?? 'Postcode hulp [not in sitecore]',
                      defaultValue: addressAutocompletePrefill.zipCode,
                      yupProps: register('zipCode'),
                    }}
                    cityFormField={{
                      label: data.cityFormField.value.label ?? 'Stad [not in sitecore]',
                      hint: data.cityFormField.value.hint ?? 'Stad hulp [not in sitecore]',
                      defaultValue: addressAutocompletePrefill.city,
                      yupProps: register('city'),
                    }}
                    streetNameFormField={
                      lockedFields?.street
                        ? undefined
                        : {
                            label: data.streetFormField.value.label ?? 'Straat [not in sitecore]',
                            hint: data.streetFormField.value.hint ?? 'Straat hulp [not in sitecore]',
                            defaultValue: addressAutocompletePrefill.street,
                            yupProps: register('street'),
                          }
                    }
                    onZipCodeCityChange={value => {
                      setValue('zipCode', value.zipCode ?? '');
                      setValue('city', value.city ?? '');
                    }}
                    onStreetNameChange={streetName => setValue('street', streetName)}
                    isApiAddressValidYupProps={register(CANCEL_ORDER_SCHEMA_FIELDS.IS_API_POSTAL_CODE_CITY_VALID)}
                    zipCodeCityError={
                      data.postalCodeCheckNotification.value.content ?? 'Postcodecontrole mislukt [not in sitecore]'
                    }
                    streetNameNotFoundWarningTitle={
                      data.streetNameNotFoundNotification.value.title ?? 'Waarschuwing [not in sitecore]'
                    }
                    streetNameNotFoundWarningContent={
                      data.streetNameNotFoundNotification.value.content ??
                      'Let op: je straatnaam is niet gevonden. Indien je straatnaam toch correct is kan je verder gaan met het formulier. [not in sitecore]'
                    }
                    shouldValidateAddressInApi
                  />
                )}
                {lockedFields?.street ? (
                  <ReadFormField
                    label={data.streetFormField.value.label ?? 'Straat [not in sitecore]'}
                    value={getValues(CANCEL_ORDER_SCHEMA_FIELDS.STREET)}
                  />
                ) : (
                  isZipCodeCityLocked && (
                    <InputText
                      error={errors.street?.message as string}
                      label={data.streetFormField.value.label ?? 'Straat [not in sitecore]'}
                      placeholder={data.streetFormField.value.placeholder ?? 'Straat placeholder [not in sitecore]'}
                      hint={data.streetFormField.value.hint ?? 'Straat hulp [not in sitecore]'}
                      {...register('street')}
                    />
                  )
                )}
                {lockedFields?.houseNumber && isBusNumberLocked ? (
                  <>
                    <ReadFormField
                      label={data.houseNumberFormField.value.label ?? 'Huisnummer [not in sitecore]'}
                      value={getValues(CANCEL_ORDER_SCHEMA_FIELDS.HOUSE_NUMBER)}
                    />
                    <ReadFormField
                      label={data.busNumberFormField.value.label ?? 'Bus [not in sitecore]'}
                      value={getValues(CANCEL_ORDER_SCHEMA_FIELDS.BUS_NUMBER)}
                    />
                  </>
                ) : (
                  <Stack gap={4} direction={{ initial: 'column', md: 'row' }}>
                    <Stack.Item grow>
                      {lockedFields?.houseNumber ? (
                        <ReadFormField
                          label={data.houseNumberFormField.value.label ?? 'Huisnummer [not in sitecore]'}
                          value={getValues(CANCEL_ORDER_SCHEMA_FIELDS.HOUSE_NUMBER)}
                        />
                      ) : (
                        <InputText
                          error={errors.houseNumber?.message as string}
                          label={data.houseNumberFormField.value.label ?? 'Huisnummer [not in sitecore]'}
                          placeholder={
                            data.houseNumberFormField.value.placeholder ?? 'Huisnummer placeholder [not in sitecore]'
                          }
                          hint={data.houseNumberFormField.value.hint ?? 'Huisnummer hulp [not in sitecore]'}
                          {...register('houseNumber')}
                        />
                      )}
                    </Stack.Item>
                    <Stack.Item grow>
                      {isBusNumberLocked ? (
                        <ReadFormField
                          label={data.busNumberFormField.value.label ?? 'Bus [not in sitecore]'}
                          value={getValues(CANCEL_ORDER_SCHEMA_FIELDS.BUS_NUMBER)}
                        />
                      ) : (
                        <InputText
                          error={errors.busNumber?.message as string}
                          label={data.busNumberFormField.value.label ?? 'Bus [not in sitecore]'}
                          placeholder={data.busNumberFormField.value.placeholder ?? 'Bus placeholder [not in sitecore]'}
                          hint={data.busNumberFormField.value.hint ?? 'Bus hulp [not in sitecore]'}
                          {...register('busNumber')}
                        />
                      )}
                    </Stack.Item>
                  </Stack>
                )}
              </Stack>
            </Stack.Item>

            <Stack.Item>
              <Controller
                control={control}
                name="product"
                render={({ field: { onChange, value } }) => (
                  <CheckboxGroup
                    label={data.productFormField.value.label ?? 'Productoptie [not in sitecore]'}
                    hint={<RichText html={data.productFormField.value.hint ?? 'Productoptie hulp [not in sitecore]'} />}
                    error={errors.product?.message as string}>
                    <Checkbox
                      label={data.productElectricityLabel.value ?? 'Elektriciteit [not in sitecore]'}
                      name="product"
                      isChecked={value?.includes('electricity')}
                      onChange={() =>
                        onChange(
                          value?.includes('electricity')
                            ? value.filter(product => product !== 'electricity')
                            : [...(value ?? []), 'electricity'],
                        )
                      }
                    />
                    <Checkbox
                      label={data.gasProductLabel.value ?? 'Gas [not in sitecore]'}
                      name="product"
                      isChecked={value?.includes('gas')}
                      onChange={() =>
                        onChange(
                          value?.includes('gas')
                            ? value.filter(product => product !== 'gas')
                            : [...(value ?? []), 'gas'],
                        )
                      }
                    />
                  </CheckboxGroup>
                )}
              />
            </Stack.Item>

            <Stack.Item>
              <Stack gap={4}>
                <TextArea
                  rows={6}
                  label={data.cancellationReasonFormField.value.label ?? 'Reden herroeping [not in sitecore]'}
                  placeholder={
                    data.cancellationReasonFormField.value.placeholder ??
                    'Reden herroeping placeholder [not in sitecore]'
                  }
                  error={errors.cancellationReason?.message as string}
                  {...register('cancellationReason')}
                />
              </Stack>
            </Stack.Item>
          </Stack>
        </Stack.Item>

        <Stack.Item>
          <Recaptcha hasError={!!errors.recaptchaToken} ref={recaptchaRef} />
        </Stack.Item>
        {error && (
          <NotificationBox
            {...error}
            text={<RichText html={data.errorNotification.value.content ?? 'Foutmelding [not in sitecore]'} />}
          />
        )}
        <Stack.Item>
          <Stack gap={6} direction="row" alignY="center">
            <SubmitButton type="submit" isLoading={isLoadingSubmit}>
              <span>{data.submitButtonText.value ?? 'Herroeping indienen [not in sitecore]'}</span>
            </SubmitButton>
            <Stack.Item>
              <RichText
                html={format(privacyStatementContent, {
                  url: `<a href="${data?.privacyStatementLink?.value?.href ?? `https://cdn.eneco.be/downloads/nl/general/vw/privacy-statement.pdf?_gl=1*10x38el*_gcl_au*MTEyMjI5MzE3My4xNzY0ODQ1NjEx*FPAU*NjA5MzE3NDQyLjE3NjQ4NDU0MDY`}" color="textBrand">${data?.privacyStatementLink?.value?.text ?? 'privacy statement'}</a>`,
                })}
                replacements={link}
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
      <AlertDialog
        title={confirmationDialog.title ?? 'Bevestig herroeping [not in sitecore]'}
        isOpen={isConfirmationOpen}
        setOpen={setIsConfirmationOpen}
        confirmText={confirmationDialog?.submitButtonText || 'Ja, herroepen [not in sitecore]'}
        denyText={confirmationDialog?.cancelButtonText || 'Nee, terug [not in sitecore]'}
        onConfirm={confirmSubmit}
        onDeny={() => {
          setPendingSubmitValues(null);
          setIsConfirmationOpen(false);
        }}
      />
    </RecaptchaFormBe>
  );
};

export default CancelOrder;

const SubmitButton = styled(Button, { minWidth: 'unset !important' });
